import { Contract, PaginatedResponse, PaginationParams } from '@console/utils';
import { apiClient } from './client';

export interface CreateContractDto {
  companyName: string;
  contractNumber: string;
  startDate: Date;
  endDate: Date;
  contractedHours: string;
  monthlyHoursLimit?: string;
  minDebitTime: string;
  accumulateHours?: boolean;
  excludeProactiveTickets?: boolean;
}

export interface UpdateContractDto {
  companyName?: string;
  contractNumber?: string;
  startDate?: Date;
  endDate?: Date;
  contractedHours?: string;
  monthlyHoursLimit?: string;
  minDebitTime?: string;
  accumulateHours?: boolean;
  excludeProactiveTickets?: boolean;
  archived?: boolean;
}

export interface RenewContractDto {
  startDate: Date;
  endDate: Date;
  contractedHours?: string;
  monthlyHoursLimit?: string;
  minDebitTime?: string;
  renewalNotes?: string;
}

export interface ContractFilters extends PaginationParams {
  status?: string;
  sortBy?: string;
  hideInactiveExpired?: boolean;
  companyId?: string;
}

export const contractsApi = {
  getAll: async (params?: ContractFilters): Promise<PaginatedResponse<Contract>> => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.append('page', params.page.toString());
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (params?.status) searchParams.append('status', params.status);
    if (params?.sortBy) searchParams.append('sortBy', params.sortBy);
    if (params?.hideInactiveExpired !== undefined)
      searchParams.append('hideInactiveExpired', params.hideInactiveExpired.toString());
    if (params?.companyId) searchParams.append('companyId', params.companyId);

    const queryString = searchParams.toString();
    const response = await apiClient.get(`/contracts${queryString ? `?${queryString}` : ''}`);
    return response.data;
  },

  getById: async (id: string): Promise<Contract> => {
    const response = await apiClient.get(`/contracts/${id}`);
    return response.data;
  },

  create: async (data: CreateContractDto): Promise<Contract> => {
    const contractData = {
      companyName: data.companyName,
      number: data.contractNumber,
      startDate: data.startDate.toISOString(),
      endDate: data.endDate.toISOString(),
      contractedHours: Number(data.contractedHours),
      ...(data.monthlyHoursLimit && { monthlyHoursLimit: Number(data.monthlyHoursLimit) }),
      minDebitTime: Number(data.minDebitTime),
      ...(data.accumulateHours !== undefined && { accumulateHours: data.accumulateHours }),
      ...(data.excludeProactiveTickets !== undefined && {
        excludeProactiveTickets: data.excludeProactiveTickets,
      }),
    };

    const response = await apiClient.post('/contracts', contractData);
    return response.data;
  },

  update: async (id: string, data: UpdateContractDto): Promise<Contract> => {
    const updateData = {
      ...(data.companyName && { companyName: data.companyName }),
      ...(data.contractNumber && { number: data.contractNumber }),
      ...(data.startDate && { startDate: data.startDate.toISOString() }),
      ...(data.endDate && { endDate: data.endDate.toISOString() }),
      ...(data.contractedHours && { contractedHours: Number(data.contractedHours) }),
      ...(data.monthlyHoursLimit && { monthlyHoursLimit: Number(data.monthlyHoursLimit) }),
      ...(data.minDebitTime && { minDebitTime: Number(data.minDebitTime) }),
      ...(data.accumulateHours !== undefined && { accumulateHours: data.accumulateHours }),
      ...(data.excludeProactiveTickets !== undefined && {
        excludeProactiveTickets: data.excludeProactiveTickets,
      }),
      ...(data.archived !== undefined && { archived: data.archived }),
    };

    const response = await apiClient.patch(`/contracts/${id}`, updateData);
    return response.data;
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/contracts/${id}`);
  },

  renew: async (id: string, data: RenewContractDto): Promise<Contract> => {
    const renewData = {
      startDate: data.startDate.toISOString(),
      endDate: data.endDate.toISOString(),
      ...(data.contractedHours && { contractedHours: Number(data.contractedHours) }),
      ...(data.monthlyHoursLimit && { monthlyHoursLimit: Number(data.monthlyHoursLimit) }),
      ...(data.minDebitTime && { minDebitTime: Number(data.minDebitTime) }),
      ...(data.renewalNotes && { renewalNotes: data.renewalNotes }),
    };

    const response = await apiClient.post(`/contracts/${id}/renew`, renewData);
    return response.data;
  },
};
